import { Component, Host, h, Listen, Prop, EventEmitter, Event, Element, Method, Watch } from '@stencil/core';

/**
 *
 * @slot - Radio group content
 */
@Component({
  tag: 'o2-radio-group',
  styleUrl: 'radio-group.scss',
})
export class O2RadioGroup {
  private radioGroupID = `o2-radio-group-${radioGroupIDs++}`;

  @Element() el!: HTMLO2RadioGroupElement;

  /**
   * Contains the selected radio value. If changed, radio group selects the radio with exact value
   */
  @Prop({ reflect: true, mutable: true }) value?: string;

  /**
   * Sets the name for radio group.
   */
  @Prop({ reflect: true }) name?: string;

  /**
   * Disables radio group
   */
  @Prop() disabled?: boolean = false;

  /**
   * Sets the required attribute of radio group
   */
  @Prop() required?: boolean = true;

  /**
   * Indicates error
   */
  @Prop() error?: boolean = false;

  /**
   * Emits when radio changes
   */
  @Event() o2Change!: EventEmitter<any>;

  /**
   * Watches the disabled prop
   */
  @Watch('disabled')
  handleDisabledChange(newVal: boolean) {
    const radios = this.getRadios();
    this.el.ariaDisabled = newVal.toString();

    for (const radio of radios) {
      radio.disabled = newVal;
    }
  }

  /**
   * Watches the error prop
   */
  @Watch('error')
  handleErrorChange(newVal: boolean) {
    const radios = this.getRadios();

    for (const radio of radios) {
      radio.error = newVal;
    }
  }

  /**
   * Watches the value prop
   */
  @Watch('value')
  handleValueChange(newVal: string) {
    this.setRadioValue(newVal);
  }

  /**
   * Listens on radios o2RadioChange event
   */
  @Listen('o2RadioChange')
  onChangeHandler(event) {
    let path = event.path || (event.composedPath && event.composedPath());
    let i = 0;

    // Get index of parent in path
    while (path[i].nodeName.toLowerCase() != 'o2-radio-group') i++;

    if (path[i].getAttribute('data-id') === this.radioGroupID) {
      this.setRadioValue(event.detail.value);

      this.o2Change.emit({
        value: event.detail.value,
        name: this.name,
      });
    }
  }

  private getRadioGroupID = radioElement => {
    let i = 0;
    let parents = [];

    for (radioElement; (radioElement = radioElement.parentNode); ) {
      parents.push(radioElement);
    }

    while (parents[i].nodeName.toLowerCase() != 'o2-radio-group') i++;

    return parents[i].getAttribute('data-id');
  };

  private getRadios = () => {
    const radios = Array.from(this.el.querySelectorAll('o2-radio'));
    return radios.filter(radio => this.getRadioGroupID(radio) === this.radioGroupID);
  };

  private setRadioValue(selected: string) {
    const radios = this.getRadios();

    radios.forEach(item => {
      if (this.getRadioGroupID(item) === this.radioGroupID) {
        if (item.getAttribute('value') === selected) {
          (item as HTMLO2RadioElement).checked = true;
          this.value = item.getAttribute('value');
          this.el.ariaChecked = this.value && this.value !== '' ? 'true' : 'false';
        } else {
          (item as HTMLO2RadioElement).checked = false;
        }
      }
    });
  }

  private setRadiosName() {
    const radios = this.getRadios();

    if (this.name !== undefined && this.name !== null && this.name !== '') {
      radios.forEach(item => {
        if (this.getRadioGroupID(item) === this.radioGroupID) {
          item.setAttribute('name', this.name);
        }
      });
    }
  }

  /**
   * Sets focus on the native `input` in `o2-input`. Use this method instead of the global
   * `input.focus()`.
   */
  @Method()
  async setFocus() {
    const radio = this.el.querySelector('o2-radio');

    if (radio) {
      radio.setFocus();
    }
  }

  /**
   * Sets blur on the native `input` in `o2-input`. Use this method instead of the global
   * `input.blur()`.
   * @internal
   */
  @Method()
  async setBlur() {
    const radio = this.el.querySelector('o2-radio');

    if (radio) {
      radio.setBlur();
    }
  }

  componentDidRender() {
    if (this.disabled) {
      const radios = this.getRadios();
      for (const radio of radios) {
        radio.disabled = true;
      }
    }

    if (this.error) {
      const radios = this.getRadios();
      for (const radio of radios) {
        radio.error = true;
      }
    }

    if (this.value) {
      this.setRadioValue(this.value);
    }

    this.setRadiosName();
  }

  render() {
    return <Host role="radiogroup" data-id={this.radioGroupID} error={this.error} aria-checked={this.value && this.value !== '' ? 'true' : 'false'} aria-disabled={this.disabled.toString()} aria-required={this.required.toString()}></Host>;
  }
}

let radioGroupIDs = 0;
